"use client";

import { useFormatDate } from "@/lib/i18n/format";
import type { Id } from "@v1/backend/convex/_generated/dataModel";
import { cn } from "@v1/ui/utils";
import { BookOpen } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactElement, ReactNode } from "react";
import { MessengerAgentAvatar, MessengerPersonAvatar } from "./messenger-avatars";
import { MessengerAttachmentChips } from "./messenger-attachments";
import { useMessenger } from "./messenger-context";
import { MessengerMarkdown } from "./messenger-markdown";
import { splitSourceLinks } from "./messenger-sources";

const GROUP_WINDOW_MS = 5 * 60 * 1000;

export interface MessengerBubbleItem {
  readonly id: string;
  readonly author: "self" | "agent" | "staff" | "contact";
  /** Null = the app's own word (Mitarbeiter, Kontakt). */
  readonly name: string | null;
  readonly text: string;
  /** Null while the answer is still streaming. */
  readonly at: number | null;
  readonly commentId?: Id<"taskComments">;
  readonly attachments?: ReadonlyArray<{ readonly file_id: Id<"files">; readonly name: string }>;
}

function sameGroup(a: MessengerBubbleItem, b: MessengerBubbleItem): boolean {
  if (a.author !== b.author || a.name !== b.name) return false;
  if (a.at === null || b.at === null) return true;
  return Math.abs(b.at - a.at) < GROUP_WINDOW_MS;
}

function useSourceChips(text: string): { body: string; chips: ReactNode } {
  const { config, nav } = useMessenger();
  const { body, sources } = splitSourceLinks(text, config.help_org_slug);
  const chips =
    sources.length === 0 ? null : (
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {sources.map((source) => (
          <button
            key={source.pageId}
            type="button"
            onClick={() => nav.push({ kind: "article", pageId: source.pageId })}
            className="inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-full bg-action-500/[0.08] px-3 text-[12px] font-medium text-primary/70 transition-colors hover:bg-action-500/[0.12]"
          >
            <BookOpen className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
            <span className="min-w-0 truncate">{source.title}</span>
          </button>
        ))}
      </div>
    );
  return { body, chips };
}

function AgentBody({ text }: { readonly text: string }): ReactElement {
  const { body, chips } = useSourceChips(text);
  return (
    <>
      {body.length > 0 ? <MessengerMarkdown text={body} /> : null}
      {chips}
    </>
  );
}

/** Thread bubbles: own messages right in green, others left with avatar. */
export function MessengerBubbles({
  items,
  taskId,
  footer,
}: {
  readonly items: readonly MessengerBubbleItem[];
  readonly taskId?: Id<"tasks">;
  readonly footer?: ReactNode;
}): ReactElement {
  const t = useTranslations("messenger.thread");
  const formatDate = useFormatDate();
  return (
    <ul className="flex min-w-0 flex-col px-4 py-4">
      {items.map((item, idx) => {
        const prev = items[idx - 1];
        const next = items[idx + 1];
        const continuesPrev = prev !== undefined && sameGroup(prev, item);
        const continuesNext = next !== undefined && sameGroup(item, next);
        const isSelf = item.author === "self";
        // The AI needs no name over its bubbles; the triangle says it. People keep theirs.
        const name =
          item.author === "agent"
            ? null
            : (item.name ?? (item.author === "staff" ? t("mitarbeiter") : t("kontakt")));
        return (
          <li
            key={item.id}
            className={cn(
              "flex min-w-0 items-end gap-2",
              isSelf ? "justify-end" : "justify-start",
              continuesPrev ? "mt-1" : "mt-4 first:mt-0",
            )}
          >
            {!isSelf ? (
              <span className={cn("shrink-0", continuesNext && "invisible")}>
                {item.author === "agent" || name === null ? (
                  <MessengerAgentAvatar size="sm" />
                ) : (
                  <MessengerPersonAvatar name={name} />
                )}
              </span>
            ) : null}
            <div className={cn("flex min-w-0 max-w-[80%] flex-col gap-1", isSelf ? "items-end" : "items-start")}>
              {!isSelf && !continuesPrev && name !== null ? (
                <span className="px-1 text-[11px] font-medium leading-tight text-primary/50">{name}</span>
              ) : null}
              <div
                className={cn(
                  "min-w-0 max-w-full rounded-2xl px-3.5 py-2 text-[14px] leading-relaxed shadow-[0_1px_2px_rgba(0,0,0,0.03)] [overflow-wrap:anywhere]",
                  isSelf ? "bg-action-500 text-white" : "bg-action-500/[0.05] text-primary/90",
                  !continuesNext && (isSelf ? "rounded-br-md" : "rounded-bl-md"),
                )}
              >
                {item.author === "agent" ? (
                  <AgentBody text={item.text} />
                ) : (
                  <p className="whitespace-pre-wrap break-words">{item.text}</p>
                )}
                {item.attachments && item.attachments.length > 0 && taskId && item.commentId ? (
                  <MessengerAttachmentChips
                    taskId={taskId}
                    commentId={item.commentId}
                    attachments={item.attachments}
                    isSelf={isSelf}
                  />
                ) : null}
              </div>
              {!continuesNext && item.at !== null ? (
                <span className="px-1 text-[10.5px] leading-tight tabular-nums text-primary/35">
                  {formatDate.time(item.at)}
                </span>
              ) : null}
            </div>
          </li>
        );
      })}
      {footer}
    </ul>
  );
}
